import React, { useCallback, useEffect, useState } from "react";
import { FaCheck, FaEye, FaEyeSlash, FaInbox, FaPlug, FaSave, FaSyncAlt } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import "./SsoSettings.css";

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

const EMPTY_FORM = {
  enabled: false,
  tenantId: "",
  clientId: "",
  clientSecret: null,
  reportingMailbox: "",
  pollIntervalSeconds: 120,
};

export default function DefenderIntegrationSettings() {
  const { token } = useAuth();

  const [form, setForm] = useState(EMPTY_FORM);
  const [hasSecret, setHasSecret] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [banner, setBanner] = useState(null); // { type, msg }
  const [lastSync, setLastSync] = useState({ at: null, status: null });

  const headers = useCallback(
    () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
    [token]
  );

  const loadConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/defender-submissions/config`, { headers: headers() });
      const data = await res.json();
      if (data.config) {
        const c = data.config;
        setForm({
          enabled: c.enabled || false,
          tenantId: c.tenantId || "",
          clientId: c.clientId || "",
          clientSecret: null,
          reportingMailbox: c.reportingMailbox || "",
          pollIntervalSeconds: c.pollIntervalSeconds || 120,
        });
        setHasSecret(c.hasClientSecret || false);
        setLastSync({ at: c.lastSyncedAt || null, status: c.lastSyncStatus || null });
      }
    } catch {
      setBanner({ type: "error", msg: "Failed to load Defender integration configuration." });
    }
  }, [headers]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const set = (field) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: val }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setBanner(null);
    setSaving(true);
    try {
      const body = { ...form, pollIntervalSeconds: Number(form.pollIntervalSeconds) || 120 };
      if (!body.clientSecret && hasSecret) delete body.clientSecret;

      const res = await fetch(`${API_BASE}/api/defender-submissions/config`, {
        method: "PUT",
        headers: headers(),
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setBanner({ type: "error", msg: data.error || "Failed to save." });
      } else {
        setBanner({ type: "success", msg: "Defender integration saved." });
        await loadConfig();
      }
    } catch {
      setBanner({ type: "error", msg: "Network error — could not save." });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setBanner(null);
    setTesting(true);
    try {
      const res = await fetch(`${API_BASE}/api/defender-submissions/config/test`, {
        method: "POST",
        headers: headers(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setBanner({ type: "error", msg: data.error || "Connection failed." });
      } else {
        setBanner({ type: "success", msg: data.message || "Connection successful." });
      }
    } catch {
      setBanner({ type: "error", msg: "Network error — could not test connection." });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="sso-page">
      <div className="sso-header">
        <div className="sso-header-icon"><FaInbox /></div>
        <div>
          <h2 className="sso-header-title">Microsoft Defender — User Submissions</h2>
          <p className="sso-header-desc">
            Automatically ingest user-reported emails from the Defender reporting mailbox so
            analysts can analyze them from the User Submissions page.
          </p>
        </div>
      </div>

      <div className="sso-guide">
        <p className="sso-guide-title">Azure App Registration — required settings</p>
        <ol className="sso-guide-list">
          <li>In <strong>Azure Portal → Entra ID → App registrations</strong>, create a registration (or reuse one).</li>
          <li>Under <strong>API permissions</strong>, add the <em>application</em> permission <strong>Mail.Read</strong> and grant admin consent.</li>
          <li>Under <strong>Certificates &amp; secrets</strong>, create a client secret and paste it below.</li>
          <li>In Exchange Online, scope the app to only the reporting mailbox with <code>New-ApplicationAccessPolicy</code> (least privilege).</li>
          <li>In <strong>Defender → Settings → User reported settings</strong>, send reported messages to that reporting mailbox.</li>
        </ol>
      </div>

      {banner && (
        <div className={`sso-banner sso-banner--${banner.type}`}>
          {banner.type === "success" && <FaCheck style={{ marginRight: 7 }} />}
          {banner.msg}
        </div>
      )}

      <form className="sso-form" onSubmit={handleSave}>
        <div className="sso-toggle-row">
          <label className="sso-toggle-label" htmlFor="defender-enabled">
            <span className="sso-toggle-text">
              <span className="sso-toggle-title">Enable ingestion</span>
              <span className="sso-toggle-sub">Allow analysts to pull user-reported emails from the reporting mailbox via "Sync now".</span>
            </span>
            <span className={`sso-toggle-pill ${form.enabled ? "on" : "off"}`}>
              <input id="defender-enabled" type="checkbox" checked={form.enabled} onChange={set("enabled")} />
              <span className="sso-pill-thumb" />
            </span>
          </label>
        </div>

        <div className="sso-section">
          <p className="sso-section-title">App Registration Credentials</p>
          <div className="sso-fields">
            <label className="sso-field">
              <span className="sso-field-label">Directory (Tenant) ID <span className="sso-required">*</span></span>
              <input className="sso-input" type="text" value={form.tenantId} onChange={set("tenantId")}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
            </label>

            <label className="sso-field">
              <span className="sso-field-label">Application (Client) ID <span className="sso-required">*</span></span>
              <input className="sso-input" type="text" value={form.clientId} onChange={set("clientId")}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
            </label>

            <label className="sso-field">
              <span className="sso-field-label">
                Client Secret {hasSecret && <span className="sso-stored">(stored — leave blank to keep)</span>}
              </span>
              <div className="sso-secret-wrap">
                <input
                  className="sso-input sso-input--secret"
                  type={showSecret ? "text" : "password"}
                  value={form.clientSecret ?? ""}
                  onChange={set("clientSecret")}
                  placeholder={hasSecret ? "Leave blank to keep existing secret" : "Paste client secret value"}
                  autoComplete="new-password"
                />
                <button type="button" className="sso-secret-toggle" onClick={() => setShowSecret((v) => !v)}
                  aria-label={showSecret ? "Hide secret" : "Show secret"}>
                  {showSecret ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </label>
          </div>
        </div>

        <div className="sso-section">
          <p className="sso-section-title">Reporting Mailbox</p>
          <div className="sso-fields">
            <label className="sso-field">
              <span className="sso-field-label">Reporting mailbox <span className="sso-required">*</span></span>
              <input className="sso-input" type="email" value={form.reportingMailbox} onChange={set("reportingMailbox")}
                placeholder="phishing-reports@yourcompany.com" />
              <span className="sso-field-help">
                The mailbox Defender forwards user-reported messages to. The app reads only this mailbox.
                Analysts pull new reports on demand from the User Submissions page using "Sync now".
              </span>
            </label>
          </div>
        </div>

        {(lastSync.at || lastSync.status) && (
          <div className="sso-section">
            <p className="sso-section-title">Last Sync</p>
            <div className="sso-fields">
              <p className="sso-field-help" style={{ margin: 0 }}>
                {lastSync.at ? `Last synced: ${new Date(lastSync.at).toLocaleString()}` : "Not synced yet."}
                {lastSync.status ? ` — ${lastSync.status}` : ""}
              </p>
            </div>
          </div>
        )}

        <div className="sso-actions">
          <button className="sso-btn sso-btn--save" type="submit" disabled={saving}>
            <FaSave />
            {saving ? "Saving…" : "Save Configuration"}
          </button>
          <button className="sso-btn" type="button" onClick={handleTest} disabled={testing}
            style={{ background: "#0ea5e9", color: "#f8fafc" }}>
            {testing ? <FaSyncAlt className="spin" /> : <FaPlug />}
            {testing ? " Testing…" : " Test connection"}
          </button>
        </div>
      </form>
    </div>
  );
}
